import axios from "axios";
import Cookies from "js-cookie";
import {store} from "../src"
import { addNotification } from "../src/features/notifications/slices/notification.slice";
import * as Sentry from "@sentry/react";
import { sanitizeError } from "./utilis/sentry-sanitization";
import { getAccountId, getFacilityId } from "./utilis/facilityStorage";
import { refreshManager } from "./services/auth/refreshManager";

const baseURL = process.env.REACT_APP_BASE_URL;

if (process.env.NODE_ENV !== "development" && (!baseURL || baseURL.includes("localhost"))) {  
  const env = process.env.NODE_ENV;
  Sentry.captureMessage(`⚠️ Invalid Axios baseURL configuration in ${env}`, "warning");
}

const instance = axios.create({
  baseURL: baseURL,
  withCredentials: true,
  credentials: "include",
});

// Normalize backend error shapes so frontend code can rely on consistent fields
const getErrorMeta = (error) => {
  const response = error?.response;
  const data = response?.data || {};
  const nestedError = data.error || {};

  const statusCode = data.statusCode || response?.status || null;
  const errorCode = data.errorCode || data.code || nestedError.code || null;
  const message =
    nestedError.message ||
    data.message ||
    data.error ||
    (statusCode === 400
      ? "Bad request"
      : statusCode === 401
      ? "Unauthorized"
      : statusCode === 403
      ? "Forbidden"
      : statusCode === 404
      ? "Not found"
      : statusCode === 429
      ? "Too many requests. Please slow down."
      : "Internal server error");

  return { statusCode, errorCode, message, data };
};

const redirectToLogin = (message = null) => {
  if (message) {
    store.dispatch(addNotification({
      type: "error",
      label: message,
      id: "sessionError",
    }));
  }
  sessionStorage.clear();
  window.location.href = '/login';
};

instance.interceptors.request.use(
  function (config) {
    const token = sessionStorage.getItem("x-auth");
    let accountId = getAccountId();
    let facilityId = getFacilityId();

    if (token) {
      config.headers["x-auth"] = token;
      config.headers["accountId"] = accountId;
      config.headers["facilityId"] = facilityId;
      config.withCredentials = true;
      config.credentials = true;
    }

    const safeMethods = ['GET', 'HEAD', 'OPTIONS'];
    const method = config.method?.toUpperCase();

    if (!safeMethods.includes(method)) {
      const csrfToken = Cookies.get("__Secure-CSRF-Token") || Cookies.get("CSRF-Token");

      if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken;
      } else if (process.env.NODE_ENV === 'development') {
        console.warn('[CSRF] Token missing for request:', config.method, config.url);
      }
    }

    return config;
  },
  function (error) {
    const sanitizedError = sanitizeError(error);
    Sentry.captureException(sanitizedError);
    Sentry.captureMessage('Axios request error', 'error');
    return Promise.reject(error);
  }
);

instance.interceptors.response.use(
  function (response) {
    return response;
  },
  async function (error) {
    const { statusCode, errorCode, message } = getErrorMeta(error);
    const requestURL = error.config?.url;
    const originalRequest = error.config;

    const sanitizedError = sanitizeError(error);
    const urlPath = requestURL ? new URL(requestURL, 'http://dummy').pathname : 'unknown';

    if (statusCode === 401) {
      if (errorCode === 'SESSION_COMPROMISED') {
        Sentry.captureMessage('Session compromised - token reuse detected', 'warning');
        redirectToLogin('Session security issue detected. Please log in again.');
        return Promise.reject(error);
      }

      if (originalRequest._retry || originalRequest.url?.includes('/refresh')) {
        redirectToLogin('Your session has expired. Please log in again.');
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      try {
        const newToken = await refreshManager.refresh();
        originalRequest.headers['x-auth'] = newToken;

        // Re-read CSRF token — refresh may have set a new one
        const freshCsrfToken = Cookies.get("__Secure-CSRF-Token") || Cookies.get("CSRF-Token");
        if (freshCsrfToken) {
          originalRequest.headers['X-CSRF-Token'] = freshCsrfToken;
        }

        return instance(originalRequest);
      } catch (refreshError) {
        const refreshErrorCode = refreshError.response?.data?.code;
        if (refreshErrorCode === 'SESSION_COMPROMISED') {
          Sentry.captureMessage('Session compromised during refresh', 'warning');
          redirectToLogin('Session security issue detected. Please log in again.');
        } else {
          redirectToLogin('Your session has expired. Please log in again.');
        }
        return Promise.reject(refreshError);
      }
    } else if (statusCode === 403) {
      Sentry.captureException(sanitizedError);
      Sentry.captureMessage(`Axios response error ${statusCode} on ${urlPath}`, 'error');

      if (errorCode === 'CSRF_TOKEN_MISSING' ||
          errorCode === 'CSRF_TOKEN_MISMATCH' ||
          errorCode === 'INVALID_ORIGIN') {

        // One silent retry with fresh CSRF token before logging out (skip for origin errors)
        if (!originalRequest._csrfRetry && errorCode !== 'INVALID_ORIGIN') {
          originalRequest._csrfRetry = true;
          const freshCsrfToken = Cookies.get("__Secure-CSRF-Token") || Cookies.get("CSRF-Token");
          if (freshCsrfToken) {
            originalRequest.headers['X-CSRF-Token'] = freshCsrfToken;
            return instance(originalRequest);
          }
        }

        store.dispatch(addNotification({
          type: "error",
          label: "Your session has expired or is invalid. Please log in again.",
          id: "csrfError",
        }));

        Sentry.captureMessage(`403 CSRF validation failed: ${errorCode}`, "warning");

        setTimeout(() => {
          sessionStorage.clear();
          window.location.href = '/login';
        }, 2000);
      } else {
        store.dispatch(addNotification({
          type: "error",
          label: "Access denied: You don't have permission to perform this action.",
          id: "forbiddenError",
        }));

        Sentry.captureMessage("403 Forbidden - Access denied", "warning");
      }
    } else if (statusCode === 429) {
      Sentry.captureException(sanitizedError);
      Sentry.captureMessage(`Axios response error ${statusCode} on ${urlPath}`, 'error');

      const retryAfter = error.response.headers["retry-after"];
      const msg = message || "Too many requests. Please slow down.";

      store.dispatch(addNotification({
        type: "error",
        label: retryAfter
          ? `${msg} Try again in ${retryAfter} seconds.`
          : `${msg}`,
        id: "rateLimitError",
      }));

      Sentry.captureMessage(`429 Rate limit triggered`, 'warning');
    } else {
      Sentry.captureException(sanitizedError);
      Sentry.captureMessage(`Axios response error ${statusCode} on ${urlPath}`, 'error');
    }

    return Promise.reject(error);
  }
);

export default instance;
